import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  asyncWithLDProvider,
  basicLogger,
  LDContext,
  useFlags as useLDFlags,
  useLDClient
} from 'launchdarkly-react-client-sdk';
import { useAuthState } from '@/contexts/AuthContext';
import { useOrganisationState } from '@/contexts/OrganisationContext';
import { sanitizeId } from '@/libs/utils';

/**
 * Type of the context value we want to provide throughout the app.
 * Includes loading state, error handling, and flag access methods.
 */
export type FeatureFlagsContextType = {
  isLoading: boolean;
  error: Error | null;
  isReady: boolean;
  updateContext?: () => Promise<void>;
};

/**
 * Props for the provider component.
 */
type FeatureFlagsProviderProps = {
  children: React.ReactNode;
};

// Create context with meaningful default values
export const FeatureFlagsContext = createContext<FeatureFlagsContextType>({
  isLoading: true,
  error: null,
  isReady: false,
  updateContext: undefined
});

// Placeholder component that renders children while LD is loading
const PlaceholderProvider: React.FC<FeatureFlagsProviderProps> = ({ children }) => <>{children}</>;

/**
 * The FeatureFlagsProvider is responsible for initializing the
 * FeatureFlags client and making it available to the entire app
 * via context.
 */
export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({ children }) => {
  const { user } = useAuthState();
  const { currentOrganisation } = useOrganisationState();

  const [LDProvider, setLDProvider] = useState<React.ComponentType<FeatureFlagsProviderProps>>(
    () => PlaceholderProvider
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isReady, setIsReady] = useState(false);

  // Memoize the context to prevent unnecessary rebuilding
  const buildLDContext = useCallback((): LDContext => {
    // Default context for anonymous users
    if (!user) {
      return {
        kind: 'user',
        key: 'anonymous'
      };
    }

    // Build user part of the context
    const userContext = {
      key: sanitizeId(user.id) || 'anonymous',
      name: user.fName ? `${user.fName} ${user.lName}` : 'anonymous',
      email: user.email,
      groups: user.access?.groups || []
    };

    // If we have organization data, use multi-context
    if (currentOrganisation) {
      return {
        kind: 'multi',
        user: userContext,
        organisation: {
          key: currentOrganisation.id,
          name: currentOrganisation.name,
          plan: currentOrganisation.plan
        }
      };
    }

    // Just user context if no organization
    return {
      kind: 'multi',
      user: userContext
    };
  }, [user, currentOrganisation]);

  // Keep track of initialization status
  const isInitialized = useRef(false);
  const initialContext = useRef(buildLDContext());

  // Initialize LaunchDarkly only once on mount
  useEffect(() => {
    let isSubscribed = true;
    setIsLoading(true);

    const clientSideID = process.env.NEXT_PUBLIC_LAUNCH_DARKLY_CLIENT_SIDE_ID;

    if (!clientSideID) {
      console.warn('LaunchDarkly client ID is missing. Feature flags will not work properly.');
      // If no client ID, skip LaunchDarkly initialization
      setIsLoading(false);
      setIsReady(false);
      return;
    }

    asyncWithLDProvider({
      clientSideID: clientSideID ?? '',
      context: initialContext.current,
      timeout: 5, // Timeout in seconds for waitForInitialization
      options: {
        streaming: true,
        bootstrap: 'localStorage',
        sendEvents: false, // Disable event sending to prevent network errors
        diagnosticOptOut: true, // Disable diagnostic events
        sendLDHeaders: false, // Reduce network requests
        useReport: false, // Use GET instead of POST requests
        logger: basicLogger({ level: 'warn' }) // Suppress info logs
      }
    })
      .then((ldProviderHOC) => {
        if (isSubscribed) {
          setLDProvider(() => ldProviderHOC);
          setIsReady(true);
          setIsLoading(false);
          isInitialized.current = true;
        }
      })
      .catch((err) => {
        if (isSubscribed) {
          console.error('Failed to initialize LaunchDarkly:', err);
          setError(err);
          setIsLoading(false);
        }
      });

    return () => {
      isSubscribed = false;
    };
  }, []); // Only run on mount, we'll handle context updates separately

  // Function to manually update the context
  const updateContextManually = useCallback(async (): Promise<void> => {
    // @ts-ignore
    const ldClient = window?.LDClient;
    if (!ldClient || !isInitialized.current) {
      return Promise.reject(new Error('LaunchDarkly client not initialized'));
    }

    const newContext = buildLDContext();

    try {
      await ldClient.identify(newContext);
      // console.debug('LaunchDarkly context updated manually');
      return Promise.resolve();
    } catch (err) {
      console.error('Error identifying with LaunchDarkly:', err);
      return Promise.reject(err);
    }
  }, [buildLDContext]);

  // Custom hook for context changes - will be accessible in child components
  const UpdateLDContextHook = () => {
    const ldClient = useLDClient();
    const prevContextRef = useRef<LDContext | null>(null);

    useEffect(() => {
      if (!ldClient || !isInitialized.current) return;

      const newContext = buildLDContext();
      const prevContext = prevContextRef.current;

      // Only update if context has actually changed
      if (JSON.stringify(newContext) !== JSON.stringify(prevContext)) {
        prevContextRef.current = newContext;

        try {
          ldClient
            .identify(newContext)
            .then(() => {
              // console.debug('LaunchDarkly context updated successfully');
            })
            .catch((err: Error) => {
              console.error('Failed to update LaunchDarkly context:', err);
            });
        } catch (err) {
          console.error('Error identifying with LaunchDarkly:', err);
        }
      }
    }, [ldClient, user, currentOrganisation]);

    return null;
  };

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      isLoading,
      error,
      isReady,
      updateContext: updateContextManually
    }),
    [isLoading, error, isReady, updateContextManually]
  );

  return (
    <FeatureFlagsContext.Provider value={contextValue}>
      <LDProvider>
        {isReady && <UpdateLDContextHook />}
        {children}
      </LDProvider>
    </FeatureFlagsContext.Provider>
  );
};

/**
 * Custom hook to access feature flags context
 */
export const useFeatureFlagsContext = () => useContext(FeatureFlagsContext);

/**
 * Re-export useFlags from LaunchDarkly for convenience
 */
export const useFlags = useLDFlags;

/**
 * Hook that combines access to a specific flag with loading state
 * @param flagKey The key of the flag to check
 * @param defaultValue Default value to use if flag is not available
 */
/**
 * Hook to manually force a context update (useful when switching organizations)
 */
export function useUpdateLDContext(): () => Promise<void> {
  const { updateContext } = useFeatureFlagsContext();

  return useCallback(async () => {
    if (updateContext) {
      return updateContext();
    }
    return Promise.reject(new Error('Context update function not available'));
  }, [updateContext]);
}

/**
 * Hook that combines access to a specific flag with loading state
 * @param flagKey The key of the flag to check
 * @param defaultValue Default value to use if flag is not available
 */
export function useFeatureFlag<T>(flagKey: string, defaultValue: T): { value: T; isLoading: boolean } {
  const flags = useFlags();
  const { isLoading } = useFeatureFlagsContext();

  const value = flags[flagKey] !== undefined ? flags[flagKey] : defaultValue;

  return useMemo(
    () => ({
      value: value as T,
      isLoading
    }),
    [value, isLoading, flagKey]
  );
}

/**
 * Returns whether i18n is enabled for the current user/organisation.
 * Falls back to the NEXT_PUBLIC_ENABLE_I18N env var when LaunchDarkly has no
 * value for the `flagUseI18n` flag (unset, provider not mounted, LD offline).
 */
export function useI18nEnabled(): boolean {
  const envDefault = process.env.NEXT_PUBLIC_ENABLE_I18N === 'true';
  const { value } = useFeatureFlag<boolean>('flagUseI18N', envDefault);
  return value;
}

export default FeatureFlagsProvider;
